import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import axios from 'axios'
import moment from 'moment-timezone'

// Set default timezone to Eastern Time
const TIMEZONE = 'America/New_York'

const BookingForm = ({ initialSlot, onSuccess, onCancel }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm({
    defaultValues: {
      facility: 'pavilion',
      start_date: initialSlot ? moment.tz(initialSlot.start, TIMEZONE).format('YYYY-MM-DD') : '',
      start_time: initialSlot ? moment.tz(initialSlot.start, TIMEZONE).format('HH:mm') : '',
      end_date: initialSlot ? moment.tz(initialSlot.end, TIMEZONE).format('YYYY-MM-DD') : '',
      end_time: initialSlot ? moment.tz(initialSlot.end, TIMEZONE).format('HH:mm') : '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      event_type: '',
      expected_attendees: '',
      event_description: '',
      special_requirements: '',
      waiver_accepted: false,
      signature: '',
      lifeguard_acknowledgment: false
    }
  })

  // Watch fields for auto-fill logic
  const waiverAccepted = watch('waiver_accepted')
  const startDate = watch('start_date')
  const startTime = watch('start_time')
  const expectedAttendees = watch('expected_attendees')

  // Generate time options in 30-minute increments from 7am to 10pm (in Eastern Time)
  const generateTimeOptions = () => {
    const times = []
    // Only allow reservations between 7am (hour 7) and 10pm (hour 22)
    for (let hour = 7; hour <= 22; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const hourStr = hour.toString().padStart(2, '0')
        const minStr = minute.toString().padStart(2, '0')
        const timeValue = `${hourStr}:${minStr}`
        const displayTime = moment.tz(timeValue, 'HH:mm', TIMEZONE).format('h:mm A')
        times.push({ value: timeValue, label: displayTime })
      }
    }
    return times
  }

  const timeOptions = generateTimeOptions()

  // Auto-fill end date when start date changes
  useEffect(() => {
    if (startDate) {
      setValue('end_date', startDate)
    }
  }, [startDate, setValue])

  // Auto-fill end time to 4 hours after start time (always, even when slot is selected) (in Eastern Time)
  useEffect(() => {
    if (startTime) {
      const endMoment = moment.tz(startTime, 'HH:mm', TIMEZONE).add(4, 'hours')
      setValue('end_time', endMoment.format('HH:mm'))
    }
  }, [startTime, setValue])

  const liabilityWaiverText = `
FACILITY RENTAL AGREEMENT AND LIABILITY WAIVER

I, the undersigned, hereby request permission to use the Lockridge Forest Swim & Tennis Club facilities for private events. I understand and agree to the following terms and conditions:

1. RESPONSIBILITY: I accept full responsibility for any damage to the facility, grounds, or equipment during my rental period. I agree to leave the facility in the same condition as I found it.

2. LIABILITY WAIVER: I hereby release, waive, discharge, and covenant not to sue Lockridge Forest Swim & Tennis Club, its board members, officers, employees, and agents from any and all liability for any loss, damage, or injury to persons or property that may occur during my use of the facility.

3. INSURANCE: I understand that I am responsible for obtaining adequate insurance coverage for my event and that the Club's insurance does not cover personal events or private rentals.

4. COMPLIANCE: I agree to comply with all club rules and regulations, including but not limited to:
   - Adhering to scheduled rental times
   - Maintaining appropriate noise levels
   - Proper disposal of all trash and recyclables
   - No smoking in or around the facility
   - No alcohol without prior written approval
   - Supervision of all attendees, especially minors

5. CLEANUP: I agree to clean the facility after use, including:
   - Removing all decorations and personal items
   - Cleaning all tables and surfaces
   - Properly disposing of all trash
   - Reporting any damages or issues immediately

6. LIFEGUARD STAFFING (POOL SEASON ONLY): During pool season, depending on the number of guests who will be using the pool, there may be a requirement for additional lifeguard(s) to be on duty. Lifeguard(s) must be acquired through Pool Management Inc and paid for by me (dependent on swim load). I understand that:
   - I am responsible for contacting Pool Management Inc to arrange and pay for additional lifeguards if required
   - If an extra guard(s) is required and is not or cannot be provided, non-member guests will not be permitted to swim without express permission from the board of directors
   - LFST assumes no liability of charges incurred due to lifeguard staffing requirements
   - All charges are my responsibility as the reserving member/host

7. CANCELLATION: I understand that the Club reserves the right to cancel my reservation due to weather, emergency, or facility maintenance. In such cases, I will be notified as soon as possible.

8. APPROVAL: I understand that this is a REQUEST for facility use and that approval is subject to board review and availability. I will receive confirmation via email once my request has been reviewed.

By signing below, I acknowledge that I have read, understood, and agree to all terms and conditions stated above, including the lifeguard staffing requirements during pool season.
  `.trim()

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true)
      setSubmitError(null)

      // Parse dates at START OF DAY in Eastern Time to avoid UTC date shift
      // For evening bookings, we want the date to remain correct even after converting to UTC
      const startDate = moment.tz(data.start_date, 'YYYY-MM-DD', TIMEZONE).startOf('day')
      const endDate = moment.tz(data.end_date, 'YYYY-MM-DD', TIMEZONE).startOf('day')

      // Prepare booking data matching the Booking model schema
      const bookingData = {
        facility: data.facility,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        startTime: data.start_time,
        endTime: data.end_time,
        contactName: data.contact_name,
        contactEmail: data.contact_email,
        contactPhone: data.contact_phone,
        eventType: data.event_type,
        numberOfGuests: parseInt(data.expected_attendees),
        eventDescription: data.event_description,
        specialRequests: data.special_requirements,
        liabilityWaiver: {
          agreed: data.waiver_accepted,
          signedBy: data.signature,
          signedDate: new Date().toISOString()
        }
      }

      // Submit to API
      await axios.post('/api/bookings', bookingData)

      setSubmitSuccess(true)
      reset()

      // Show success message briefly before closing
      setTimeout(() => {
        if (onSuccess) {
          onSuccess()
        }
      }, 2000)
    } catch (error) {
      console.error('Error submitting booking:', error)
      setSubmitError(
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to submit booking request. Please try again.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitSuccess) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Request Submitted Successfully!
        </h3>
        <p className="text-gray-600">
          Your facility reservation request has been submitted for board review.
          You will receive a confirmation email once your request has been processed.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {submitError && (
        <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{submitError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Facility Selection */}
      <div>
        <label htmlFor="facility" className="block text-sm font-semibold text-gray-700 mb-2">
          Facility <span className="text-red-500">*</span>
        </label>
        <select
          id="facility"
          {...register('facility', { required: 'Please select a facility' })}
          className="input"
        >
          <option value="pavilion">Pavilion</option>
          <option value="taj_mahal">Taj Mahal</option>
        </select>
        {errors.facility && (
          <p className="mt-1 text-sm text-red-600">{errors.facility.message}</p>
        )}
      </div>

      {/* Date and Time Fields */}
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="start_date" className="block text-sm font-semibold text-gray-700 mb-2">
            Start Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            id="start_date"
            {...register('start_date', {
              required: 'Start date is required',
              validate: value => {
                const selectedDate = moment.tz(value, TIMEZONE).startOf('day')
                const today = moment.tz(TIMEZONE).startOf('day')
                return selectedDate.isSameOrAfter(today) || 'Start date cannot be in the past'
              }
            })}
            className="input"
          />
          {errors.start_date && (
            <p className="mt-1 text-sm text-red-600">{errors.start_date.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="start_time" className="block text-sm font-semibold text-gray-700 mb-2">
            Start Time <span className="text-red-500">*</span>
          </label>
          <select
            id="start_time"
            {...register('start_time', { required: 'Start time is required' })}
            className="input"
          >
            <option value="">Select time</option>
            {timeOptions.map(time => (
              <option key={time.value} value={time.value}>
                {time.label}
              </option>
            ))}
          </select>
          {errors.start_time && (
            <p className="mt-1 text-sm text-red-600">{errors.start_time.message}</p>
          )}
        </div>
      </div>

      {/* Hidden end_date field - automatically set to match start_date (no multi-day reservations) */}
      <input type="hidden" {...register('end_date', { required: 'End date is required' })} />

      <div>
        <div>
          <label htmlFor="end_time" className="block text-sm font-semibold text-gray-700 mb-2">
            End Time <span className="text-red-500">*</span>
          </label>
          <select
            id="end_time"
            {...register('end_time', {
              required: 'End time is required',
              validate: (value) => {
                const startDate = watch('start_date')
                const endDate = watch('end_date')
                const startTime = watch('start_time')

                if (!startDate || !endDate || !startTime) return true

                const start = moment.tz(`${startDate} ${startTime}`, 'YYYY-MM-DD HH:mm', TIMEZONE)
                const end = moment.tz(`${endDate} ${value}`, 'YYYY-MM-DD HH:mm', TIMEZONE)

                // Check if end time is after 10pm (22:00)
                const endHour = parseInt(value.split(':')[0])
                const endMinute = parseInt(value.split(':')[1])
                if (endHour > 22 || (endHour === 22 && endMinute > 0)) {
                  return 'End time cannot be later than 10:00 PM'
                }

                // Check if end is after start
                if (!end.isAfter(start)) {
                  return 'End time must be after start time'
                }

                // Check minimum 1 hour duration
                const duration = moment.duration(end.diff(start))
                const hours = duration.asHours()
                if (hours < 1) {
                  return 'Minimum booking time is 1 hour'
                }

                return true
              }
            })}
            className="input"
          >
            <option value="">Select time</option>
            {timeOptions.map(time => (
              <option key={time.value} value={time.value}>
                {time.label}
              </option>
            ))}
          </select>
          {errors.end_time && (
            <p className="mt-1 text-sm text-red-600">{errors.end_time.message}</p>
          )}
          {!errors.end_time && startTime && (
            <p className="mt-1 text-xs text-blue-600">
              Default is 4 hours after start time. You can adjust this if needed.
            </p>
          )}
        </div>
      </div>

      {/* Contact Information */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>

        <div className="space-y-4">
          <div>
            <label htmlFor="contact_name" className="block text-sm font-semibold text-gray-700 mb-2">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="contact_name"
              {...register('contact_name', {
                required: 'Name is required',
                minLength: { value: 2, message: 'Name must be at least 2 characters' }
              })}
              className="input"
              placeholder="John Doe"
            />
            {errors.contact_name && (
              <p className="mt-1 text-sm text-red-600">{errors.contact_name.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="contact_email" className="block text-sm font-semibold text-gray-700 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="contact_email"
              {...register('contact_email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              className="input"
              placeholder="john@example.com"
            />
            {errors.contact_email && (
              <p className="mt-1 text-sm text-red-600">{errors.contact_email.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="contact_phone" className="block text-sm font-semibold text-gray-700 mb-2">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="contact_phone"
              {...register('contact_phone', {
                required: 'Phone number is required',
                pattern: {
                  value: /^[\d\s\-\(\)\.]+$/,
                  message: 'Invalid phone number'
                }
              })}
              className="input"
              placeholder="(555) 123-4567"
            />
            {errors.contact_phone && (
              <p className="mt-1 text-sm text-red-600">{errors.contact_phone.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Event Details */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Event Details</h3>

        <div className="space-y-4">
          <div>
            <label htmlFor="event_type" className="block text-sm font-semibold text-gray-700 mb-2">
              Event Type <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="event_type"
              {...register('event_type', { required: 'Event type is required' })}
              className="input"
              placeholder="e.g., Birthday Party, Family Reunion, Club Meeting"
            />
            {errors.event_type && (
              <p className="mt-1 text-sm text-red-600">{errors.event_type.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="expected_attendees" className="block text-sm font-semibold text-gray-700 mb-2">
              Expected Number of Attendees <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              id="expected_attendees"
              {...register('expected_attendees', {
                required: 'Expected attendees is required',
                min: { value: 1, message: 'Must have at least 1 attendee' },
                max: { value: 500, message: 'Maximum 500 attendees' }
              })}
              className="input"
              placeholder="25"
              min="1"
            />
            {errors.expected_attendees && (
              <p className="mt-1 text-sm text-red-600">{errors.expected_attendees.message}</p>
            )}
          </div>

          {/* Lifeguard Acknowledgment for 11+ attendees */}
          {expectedAttendees && parseInt(expectedAttendees) >= 11 && (
            <div className="border-l-4 border-orange-500 bg-orange-50 p-4 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-orange-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-bold text-orange-800 mb-2">
                    Additional Lifeguard Required
                  </h4>
                  <p className="text-sm text-orange-700 mb-3">
                    For events with 11 or more attendees who will be using the pool, you must contact Pool Management Inc to arrange and pay for additional lifeguard coverage.
                  </p>
                  <div className="bg-white border border-orange-200 rounded p-3 mb-3">
                    <p className="text-sm font-semibold text-gray-900 mb-1">Pool Management Company Contact:</p>
                    <p className="text-sm text-gray-700">
                      <strong>Pool Management Inc</strong><br />
                      Phone: <a href="tel:+14043213040" className="text-blue-600 hover:underline">(404) 321-3040</a><br />
                      Email: <a href="mailto:info@poolmanagementinc.com" className="text-blue-600 hover:underline">info@poolmanagementinc.com</a>
                    </p>
                  </div>
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="lifeguard_acknowledgment"
                      {...register('lifeguard_acknowledgment', {
                        required: parseInt(expectedAttendees) >= 11 ? 'You must acknowledge that you have contacted Pool Management Inc for additional lifeguard coverage' : false
                      })}
                      className="mt-1 h-5 w-5 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <label htmlFor="lifeguard_acknowledgment" className="ml-3 text-sm">
                      <span className="font-semibold text-gray-900">
                        I acknowledge that I have contacted Pool Management Inc to arrange additional lifeguard coverage for my event.
                      </span>
                      <span className="text-red-500"> *</span>
                    </label>
                  </div>
                  {errors.lifeguard_acknowledgment && (
                    <p className="mt-2 text-sm text-red-600">{errors.lifeguard_acknowledgment.message}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="event_description" className="block text-sm font-semibold text-gray-700 mb-2">
              Event Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="event_description"
              {...register('event_description', {
                required: 'Event description is required',
                minLength: { value: 10, message: 'Description must be at least 10 characters' }
              })}
              className="input min-h-[100px]"
              placeholder="Please provide a brief description of your event..."
              rows={4}
            />
            {errors.event_description && (
              <p className="mt-1 text-sm text-red-600">{errors.event_description.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="special_requirements" className="block text-sm font-semibold text-gray-700 mb-2">
              Special Requirements or Notes
            </label>
            <textarea
              id="special_requirements"
              {...register('special_requirements')}
              className="input min-h-[80px]"
              placeholder="Any special setup, equipment needs, or other requirements..."
              rows={3}
            />
          </div>
        </div>
      </div>

      {/* Liability Waiver */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Liability Waiver & Agreement
        </h3>

        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 max-h-64 overflow-y-auto mb-4">
          <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
            {liabilityWaiverText}
          </pre>
        </div>

        <div className="space-y-4">
          <div className="flex items-start">
            <input
              type="checkbox"
              id="waiver_accepted"
              {...register('waiver_accepted', {
                required: 'You must accept the liability waiver to proceed'
              })}
              className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="waiver_accepted" className="ml-3 text-sm text-gray-700">
              <span className="font-semibold">I have read and agree to the terms and conditions stated above.</span>
              <span className="text-red-500"> *</span>
            </label>
          </div>
          {errors.waiver_accepted && (
            <p className="text-sm text-red-600">{errors.waiver_accepted.message}</p>
          )}

          <div>
            <label htmlFor="signature" className="block text-sm font-semibold text-gray-700 mb-2">
              Electronic Signature <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="signature"
              {...register('signature', {
                required: 'Signature is required',
                validate: value => {
                  const name = watch('contact_name')
                  if (!name) return true
                  return value.toLowerCase().includes(name.split(' ')[0].toLowerCase()) ||
                         'Signature must include your first name'
                }
              })}
              className="input font-signature text-xl"
              placeholder="Type your full name here"
              disabled={!waiverAccepted}
            />
            {errors.signature && (
              <p className="mt-1 text-sm text-red-600">{errors.signature.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              By typing your name above, you agree that this constitutes a legal signature.
            </p>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="border-t pt-6 flex flex-col sm:flex-row gap-3 justify-end">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="btn px-6 py-3 border-2 border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded-lg font-medium transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Submitting...
            </span>
          ) : (
            'Submit Reservation Request'
          )}
        </button>
      </div>
    </form>
  )
}

export default BookingForm
